import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { attachmentsAPI, claimsAPI } from '../services/api';

const Uploads = () => {
  const { reference } = useParams(); // Get claim reference from URL if present
  const [attachments, setAttachments] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    claim_id: '',
    file: null,
    description: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (reference) {
        // If viewing claim-specific attachments
        const claimResponse = await claimsAPI.getById(reference);
        setClaim(claimResponse.data);

        // Set the claim_id in upload form
        setUploadForm(prev => ({ ...prev, claim_id: claimResponse.data.id }));

        const response = await claimsAPI.getAttachments(reference);
        setAttachments(response.data || response || []);
      } else {
        // If viewing all attachments
        const [attachmentsRes, claimsRes] = await Promise.all([
          attachmentsAPI.getAll(),
          fetch('/api/claims', {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json())
        ]);
        setAttachments(attachmentsRes.data || attachmentsRes || []);
        setClaims(claimsRes || []);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      const allowedTypes = ['application/pdf', 'application/msword',
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only PDF, Word documents, and images are allowed');
        return;
      }
      setUploadForm({ ...uploadForm, file });
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.claim_id || !uploadForm.file) {
      setError('Please select a claim and file');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('claim_id', uploadForm.claim_id);
      formData.append('description', uploadForm.description);
      await attachmentsAPI.upload(formData);
      setUploadForm({ claim_id: reference ? uploadForm.claim_id : '', file: null, description: '' });
      document.getElementById('fileInput').value = '';
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (id, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/attachments/${id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download file');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    try {
      await attachmentsAPI.delete(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete attachment');
      console.error(err);
    }
  };

  if (loading) return <Layout><div>Loading...</div></Layout>;

  return (
    <Layout>
      {reference && claim && (
        <div style={{ marginBottom: '2rem', fontSize: '0.9rem', color: '#666' }}>
          <Link to="/claims" style={{ color: '#4A90E2', textDecoration: 'none' }}>
            My Claims
          </Link>
          {' '}&gt;{' '}
          <Link to={`/claims/${reference}`} style={{ color: '#4A90E2', textDecoration: 'none' }}>
            {claim.claim_reference}
          </Link>
          {' '}&gt;{' '}
          <span>Attachments</span>
        </div>
      )}

      <h2>{reference ? `Attachments for ${claim?.claim_reference || 'Claim'}` : 'Document Uploads'}</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>Upload and manage documents related to your claims</p>

      {error && <div style={{ padding: '1rem', background: '#fee', color: '#c00', borderRadius: '4px', marginBottom: '1rem' }}>{error}</div>}

      <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '2rem' }}>
        <h3 style={{ marginTop: 0 }}>Upload New Document</h3>
        <form onSubmit={handleUpload}>
          {!reference && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select Claim *</label>
              <select
                value={uploadForm.claim_id}
                onChange={(e) => setUploadForm({ ...uploadForm, claim_id: e.target.value })}
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">-- Select a claim --</option>
                {claims.map(claim => (
                  <option key={claim.id} value={claim.id}>{claim.claim_reference} - {claim.claim_type}</option>
                ))}
              </select>
            </div>
          )}

          {reference && claim && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#e3f2fd', borderRadius: '4px', color: '#1565c0' }}>
              Uploading to: {claim.claim_reference} - {claim.claim_type || 'Claim'}
            </div>
          )}

          <div
            style={{
              marginBottom: '1rem',
              border: dragActive ? '2px dashed #27ae60' : '2px dashed #ddd',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              background: dragActive ? '#f0fff0' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <input
              type="file"
              id="fileInput"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📎</div>
            {uploadForm.file ? (
              <p style={{ margin: 0, color: '#27ae60', fontWeight: '500' }}>Selected: {uploadForm.file.name}</p>
            ) : (
              <>
                <p style={{ margin: '0.5rem 0', fontWeight: '500' }}>Drag and drop your file here</p>
                <p style={{ margin: '0.5rem 0', color: '#666' }}>or click to browse</p>
                <small style={{ color: '#999' }}>Maximum file size: 10MB. Allowed formats: PDF, Word, JPEG, PNG, GIF</small>
              </>
            )}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Description (Optional)</label>
            <textarea
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              placeholder="Add a description for this document..."
              rows="3"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <button
            type="submit"
            disabled={uploading || !uploadForm.file || !uploadForm.claim_id}
            style={{
              padding: '0.75rem 2rem',
              background: (uploading || !uploadForm.file || !uploadForm.claim_id) ? '#ccc' : '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (uploading || !uploadForm.file || !uploadForm.claim_id) ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </form>
      </div>

      <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0 }}>Your Documents ({attachments.length})</h3>

        {attachments.length === 0 ? (
          <p style={{ color: '#666' }}>No documents uploaded yet. Upload your first document above.</p>
        ) : (
          <div>
            {attachments.map(attachment => (
              <div key={attachment.id} style={{ padding: '1rem', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>{attachment.file_name}</h4>
                  {attachment.description && <p style={{ margin: '0.25rem 0', color: '#666' }}>{attachment.description}</p>}
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#888' }}>
                    {!reference && `Claim: ${attachment.claim_reference || 'N/A'} | `}Size: {(attachment.file_size / 1024).toFixed(1)} KB | Uploaded: {new Date(attachment.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => handleDownload(attachment.id, attachment.file_name)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Uploads;
